import { officialCardForPal, officialCardImage, OFFICIAL_CARDS, SET_NAMES } from "./tcg-cards";

// Procedural Pal dataset for Pal Stars.
// Deterministic: same slug -> same stats every render (SSR-safe).

export type Element =
  | "Neutral"
  | "Fire"
  | "Water"
  | "Grass"
  | "Electric"
  | "Ice"
  | "Ground"
  | "Dark"
  | "Dragon";

export const ELEMENTS: Element[] = [
  "Neutral", "Fire", "Water", "Grass", "Electric", "Ice", "Ground", "Dark", "Dragon",
];

export const ELEMENT_COLORS: Record<Element, string> = {
  Neutral: "#94A3B8",
  Fire: "#F97316",
  Water: "#38BDF8",
  Grass: "#22C55E",
  Electric: "#EAB308",
  Ice: "#7DD3FC",
  Ground: "#A16207",
  Dark: "#8B5CF6",
  Dragon: "#EF4444",
};

export type CardColor = "Red" | "Blue" | "Green" | "Purple" | "Colorless";

export interface PalCardData {
  cardNumber: string;
  set: string;
  cardTitle: string;
  cardType: "Pal" | "Lucky Pal";
  color: CardColor;
  cost: number;
  power: number;
  strike: number;
  rarityCode: "C" | "U" | "R" | "RR" | "SR" | "SP" | "SSP" | "OSR" | "SSS" | "TD" | "TSR" | "TSP" | "PR" | "PSD";
  /** True when the card number is the exact code printed on a real OCG card. */
  official?: boolean;
  /** Other printed variants of the same card (parallels, alt-arts). */
  variants?: { code: string; rarity: string }[];
  workIcons: string[];
  keywords: string[];
  cardText: string;
  flavorText: string;
  imageUrl?: string;
  sourceUrl: string;
}

export interface GearCardData {
  cardNumber: string;
  name: string;
  gearType: "Weapon" | "Armor" | "Tool" | "Sphere" | "Mount Gear";
  color: CardColor;
  cost: number;
  durability: number;
  rarityCode: PalCardData["rarityCode"];
  stats: { label: string; value: string }[];
  cardText: string;
  sourceUrl: string;
}

export interface StructureCardData {
  cardNumber: string;
  name: string;
  structureType: "Base" | "Production" | "Defense" | "Research" | "Raid";
  color: CardColor;
  cost: number;
  durability: number;
  rarityCode: PalCardData["rarityCode"];
  stats: { label: string; value: string }[];
  cardText: string;
  sourceUrl: string;
}

export interface EventCardData {
  cardNumber: string;
  name: string;
  eventType: "Instant" | "Ongoing" | "Support" | "Reaction";
  color: CardColor;
  cost: number;
  rarityCode: PalCardData["rarityCode"];
  cardText: string;
  flavorText: string;
  sourceUrl: string;
}

export const GEAR_CARDS: GearCardData[] = [
  {
    cardNumber: "ECG-001",
    name: "Pal Sphere",
    gearType: "Sphere",
    color: "Blue",
    cost: 1,
    durability: 100,
    rarityCode: "C",
    stats: [{ label: "Capture", value: "+10% vs weakened Pals" }, { label: "Range", value: "Short" }],
    cardText: "When played, search the top 3 cards of your deck for a Cost 2 or lower Pal and reveal it.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECG-002",
    name: "Legendary Sphere",
    gearType: "Sphere",
    color: "Purple",
    cost: 5,
    durability: 500,
    rarityCode: "SR",
    stats: [{ label: "Capture", value: "+45% vs bosses" }, { label: "Range", value: "Long" }],
    cardText: "Attach to a Legendary Pal. It gains +300 power and cannot be returned to hand by Event cards.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECG-003",
    name: "Crossbow",
    gearType: "Weapon",
    color: "Green",
    cost: 2,
    durability: 220,
    rarityCode: "U",
    stats: [{ label: "Damage", value: "120" }, { label: "Crit", value: "+10%" }],
    cardText: "Your active Pal's next strike deals +100 power if the defending Pal is Flying.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECG-004",
    name: "Rocket Launcher",
    gearType: "Weapon",
    color: "Red",
    cost: 7,
    durability: 650,
    rarityCode: "R",
    stats: [{ label: "Damage", value: "900" }, { label: "Cooldown", value: "Long" }],
    cardText: "Exhaust this Gear: deal 600 damage to one opposing Pal. Discard one card after use.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECG-005",
    name: "Jetragon Missile Saddle",
    gearType: "Mount Gear",
    color: "Purple",
    cost: 8,
    durability: 800,
    rarityCode: "SR",
    stats: [{ label: "Fly speed", value: "+400" }, { label: "Strike", value: "+1" }],
    cardText: "Can attach only to a Dragon Pal. Mounted attacks deal +300 power to Structures.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECG-006",
    name: "Assault Rifle",
    gearType: "Weapon",
    color: "Colorless",
    cost: 4,
    durability: 420,
    rarityCode: "R",
    stats: [{ label: "Damage", value: "320" }, { label: "Hits", value: "Multi" }],
    cardText: "The attached Pal may strike twice this turn. The second strike deals half power.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECG-007",
    name: "Old Bow",
    gearType: "Weapon",
    color: "Green",
    cost: 1,
    durability: 150,
    rarityCode: "C",
    stats: [{ label: "Damage", value: "60" }, { label: "Range", value: "Medium" }],
    cardText: "The attached Pal deals +50 power when striking a Pal with lower power.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECG-008",
    name: "Handgun",
    gearType: "Weapon",
    color: "Colorless",
    cost: 2,
    durability: 200,
    rarityCode: "C",
    stats: [{ label: "Damage", value: "150" }, { label: "Range", value: "Medium" }],
    cardText: "Once per turn: deal 100 damage to a benched Pal.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECG-009",
    name: "Pump-Action Shotgun",
    gearType: "Weapon",
    color: "Red",
    cost: 3,
    durability: 300,
    rarityCode: "U",
    stats: [{ label: "Damage", value: "250" }, { label: "Range", value: "Close" }],
    cardText: "The attached Pal ignores 100 defense when striking Structures.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECG-010",
    name: "Grenade Launcher",
    gearType: "Weapon",
    color: "Red",
    cost: 5,
    durability: 480,
    rarityCode: "R",
    stats: [{ label: "Damage", value: "450 splash" }, { label: "Range", value: "Long" }],
    cardText: "When played: deal 200 damage to all opposing benched Pals.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECG-011",
    name: "Meteor Cannon",
    gearType: "Weapon",
    color: "Purple",
    cost: 9,
    durability: 900,
    rarityCode: "SR",
    stats: [{ label: "Damage", value: "1200" }, { label: "Charge", value: "2 turns" }],
    cardText: "Skip your next draw: deal 1200 damage to any target. Ignores Legendary immunity.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECG-012",
    name: "Metal Armor",
    gearType: "Armor",
    color: "Colorless",
    cost: 3,
    durability: 400,
    rarityCode: "U",
    stats: [{ label: "Defense", value: "+120" }, { label: "Weight", value: "Medium" }],
    cardText: "Attached Pal takes -80 damage from all sources. Cannot be attached to Flying Pals.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECG-013",
    name: "Refined Metal Armor",
    gearType: "Armor",
    color: "Blue",
    cost: 5,
    durability: 620,
    rarityCode: "R",
    stats: [{ label: "Defense", value: "+220" }, { label: "Resist", value: "Fire/Ice" }],
    cardText: "Attached Pal ignores the first attack each turn if that attack deals ≤ 200 damage.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECG-014",
    name: "Homing Sphere Launcher",
    gearType: "Weapon",
    color: "Blue",
    cost: 6,
    durability: 520,
    rarityCode: "R",
    stats: [{ label: "Damage", value: "380" }, { label: "Homing", value: "Yes" }],
    cardText: "Cannot be blocked by Structures. Deals +100 damage to Boss Pals.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECG-015",
    name: "Ancient Civilization Parts",
    gearType: "Tool",
    color: "Purple",
    cost: 4,
    durability: 250,
    rarityCode: "SR",
    stats: [{ label: "Crafting", value: "+3 slots" }, { label: "Rare Yield", value: "+25%" }],
    cardText: "Exhaust: search your deck for one Legendary Gear and put it into your hand.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECG-016",
    name: "Frostallion Saddle",
    gearType: "Mount Gear",
    color: "Blue",
    cost: 7,
    durability: 700,
    rarityCode: "SR",
    stats: [{ label: "Fly speed", value: "+320" }, { label: "Ice damage", value: "+200" }],
    cardText: "Attach only to an Ice Pal. Grants Freeze: opposing Pal cannot strike next turn.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECG-017",
    name: "Eidrolon Void Barding",
    gearType: "Mount Gear",
    color: "Purple",
    cost: 10,
    durability: 1000,
    rarityCode: "TD",
    stats: [{ label: "Fly speed", value: "+500" }, { label: "Void damage", value: "+400" }],
    cardText: "Attach only to Eidrolon. Attached Pal is immune to Weapon and Structure destruction.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECG-018",
    name: "Old Wand",
    gearType: "Weapon",
    color: "Purple",
    cost: 2,
    durability: 180,
    rarityCode: "C",
    stats: [{ label: "Damage", value: "70" }, { label: "Element", value: "Dark" }],
    cardText: "When the attached Pal attacks a Grass Pal, deal +40 additional power.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECG-019",
    name: "Fire Bow",
    gearType: "Weapon",
    color: "Red",
    cost: 3,
    durability: 220,
    rarityCode: "U",
    stats: [{ label: "Damage", value: "110" }, { label: "Element", value: "Fire" }],
    cardText: "Ignites the target: at end of turn, deal 30 damage to a Pal hit by this Weapon.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECG-020",
    name: "Crossbow",
    gearType: "Weapon",
    color: "Green",
    cost: 3,
    durability: 240,
    rarityCode: "U",
    stats: [{ label: "Damage", value: "120" }, { label: "Range", value: "Long" }],
    cardText: "May target a Pal in your opponent's back row instead of the front.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECG-021",
    name: "Musket",
    gearType: "Weapon",
    color: "Colorless",
    cost: 4,
    durability: 260,
    rarityCode: "R",
    stats: [{ label: "Damage", value: "160" }, { label: "Range", value: "Long" }],
    cardText: "Ignores +50 of the target Pal's defensive keywords.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECG-022",
    name: "Assault Rifle",
    gearType: "Weapon",
    color: "Colorless",
    cost: 5,
    durability: 300,
    rarityCode: "R",
    stats: [{ label: "Damage", value: "200" }, { label: "Bursts", value: "3-hit" }],
    cardText: "Fires 3 times per Attack Phase; each hit deals 70 power. -20 durability per Phase.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECG-023",
    name: "Pump-Action Shotgun",
    gearType: "Weapon",
    color: "Red",
    cost: 5,
    durability: 280,
    rarityCode: "R",
    stats: [{ label: "Damage", value: "220" }, { label: "Range", value: "Short" }],
    cardText: "Deal +100 power when attacking a Pal in the front row.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECG-024",
    name: "Rocket Launcher",
    gearType: "Weapon",
    color: "Red",
    cost: 7,
    durability: 320,
    rarityCode: "RR",
    stats: [{ label: "Damage", value: "300" }, { label: "AoE", value: "Splash 60" }],
    cardText: "Deals 60 splash damage to all adjacent Pals. Consumes 1 Ammo counter.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECG-025",
    name: "Grenade",
    gearType: "Weapon",
    color: "Red",
    cost: 2,
    durability: 100,
    rarityCode: "U",
    stats: [{ label: "Damage", value: "90" }, { label: "AoE", value: "All front row" }],
    cardText: "One-time use. Deal 90 power to each opposing front-row Pal, then discard.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECG-026",
    name: "Flamethrower",
    gearType: "Weapon",
    color: "Red",
    cost: 6,
    durability: 300,
    rarityCode: "RR",
    stats: [{ label: "Damage", value: "180" }, { label: "Element", value: "Fire" }],
    cardText: "Grass Pals hit by this Weapon cannot attack next turn.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECG-027",
    name: "Laser Rifle",
    gearType: "Weapon",
    color: "Blue",
    cost: 6,
    durability: 320,
    rarityCode: "RR",
    stats: [{ label: "Damage", value: "210" }, { label: "Element", value: "Electric" }],
    cardText: "Ignores Structure defense. Stuns Electric-weak Pals for 1 turn.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECG-028",
    name: "Meteor Hammer",
    gearType: "Weapon",
    color: "Colorless",
    cost: 4,
    durability: 260,
    rarityCode: "R",
    stats: [{ label: "Damage", value: "170" }, { label: "Range", value: "Melee" }],
    cardText: "Knocks the target back to the bench; opponent chooses replacement.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECG-029",
    name: "Legendary Assault Rifle",
    gearType: "Weapon",
    color: "Colorless",
    cost: 8,
    durability: 400,
    rarityCode: "SR",
    stats: [{ label: "Damage", value: "260" }, { label: "Bursts", value: "4-hit" }],
    cardText: "Fires 4 times per Attack Phase at 90 power each. Ignores Cover keyword.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECG-030",
    name: "Legendary Rocket Launcher",
    gearType: "Weapon",
    color: "Red",
    cost: 9,
    durability: 450,
    rarityCode: "SR",
    stats: [{ label: "Damage", value: "380" }, { label: "AoE", value: "Splash 120" }],
    cardText: "Deals 120 splash to all opposing Pals. Cannot target flying Pals.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECG-031",
    name: "Eidrolon Void Cannon",
    gearType: "Weapon",
    color: "Purple",
    cost: 10,
    durability: 500,
    rarityCode: "TD",
    stats: [{ label: "Damage", value: "500" }, { label: "Element", value: "Void" }],
    cardText: "Attach only to Eidrolon. Ignores all defensive keywords. Cannot be destroyed by Events.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
];

export const STRUCTURE_CARDS: StructureCardData[] = [
  {
    cardNumber: "ECS-001",
    name: "Palbox",
    structureType: "Base",
    color: "Blue",
    cost: 2,
    durability: 700,
    rarityCode: "C",
    stats: [{ label: "Base slots", value: "+3" }, { label: "Respawn", value: "Enabled" }],
    cardText: "Once per turn, move one benched Pal to active. If it has Handiwork, draw a card.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECS-002",
    name: "Breeding Farm",
    structureType: "Production",
    color: "Green",
    cost: 4,
    durability: 600,
    rarityCode: "R",
    stats: [{ label: "Egg timer", value: "-25%" }, { label: "Cake", value: "Consumes 1" }],
    cardText: "Exhaust two compatible Pals: reveal cards until you reveal a Pal, then place it into your hand.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECS-003",
    name: "Sphere Assembly Line",
    structureType: "Production",
    color: "Colorless",
    cost: 3,
    durability: 500,
    rarityCode: "U",
    stats: [{ label: "Craft speed", value: "+30%" }, { label: "Requires", value: "Handiwork" }],
    cardText: "At the start of your turn, reduce the cost of your next Gear card by 1.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECS-004",
    name: "Electric Generator",
    structureType: "Production",
    color: "Blue",
    cost: 3,
    durability: 450,
    rarityCode: "U",
    stats: [{ label: "Power", value: "+2 energy" }, { label: "Best Pal", value: "Orserk" }],
    cardText: "Electric Pals you control gain +100 power. If Orserk is active, gain one additional energy.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECS-005",
    name: "World Tree Raid Gate",
    structureType: "Raid",
    color: "Purple",
    cost: 9,
    durability: 1200,
    rarityCode: "SR",
    stats: [{ label: "Boss", value: "Eidrolon" }, { label: "Reward", value: "Legendary schematic" }],
    cardText: "When this Structure enters play, search your deck for Eidrolon and put it into your hand.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECS-006",
    name: "Wood Wall",
    structureType: "Defense",
    color: "Green",
    cost: 1,
    durability: 200,
    rarityCode: "C",
    stats: [{ label: "Block", value: "-80 dmg" }, { label: "Slots", value: "1" }],
    cardText: "Redirect the first strike each turn from a Pal to this Structure.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECS-007",
    name: "Stone Defense Wall",
    structureType: "Defense",
    color: "Colorless",
    cost: 2,
    durability: 380,
    rarityCode: "U",
    stats: [{ label: "Block", value: "-160 dmg" }, { label: "Slots", value: "2" }],
    cardText: "Adjacent Structures gain +100 durability.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECS-008",
    name: "Ranch",
    structureType: "Production",
    color: "Green",
    cost: 2,
    durability: 300,
    rarityCode: "C",
    stats: [{ label: "Yield", value: "+1 food" }, { label: "Pals", value: "1 grazing" }],
    cardText: "At the start of your turn, gain 1 Food resource token.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECS-009",
    name: "Ore Mining Site",
    structureType: "Production",
    color: "Colorless",
    cost: 3,
    durability: 420,
    rarityCode: "U",
    stats: [{ label: "Yield", value: "+2 ore" }, { label: "Requires", value: "Mining" }],
    cardText: "Exhaust a Mining Pal: draw a card and gain 1 Ore token.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECS-010",
    name: "Repair Bench",
    structureType: "Research",
    color: "Blue",
    cost: 2,
    durability: 260,
    rarityCode: "C",
    stats: [{ label: "Repair", value: "+80 dura" }, { label: "Uses", value: "3" }],
    cardText: "Once per turn: restore 80 durability to a Gear or Structure you control.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECS-011",
    name: "Tower of Rayne",
    structureType: "Raid",
    color: "Red",
    cost: 7,
    durability: 950,
    rarityCode: "SR",
    stats: [{ label: "Boss", value: "Zoe & Grizzbolt" }, { label: "Reward", value: "Legendary schematic" }],
    cardText: "When a Boss Pal enters play, this Structure deals 300 damage to that Pal.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECS-012",
    name: "Palgenesis Chamber",
    structureType: "Research",
    color: "Purple",
    cost: 6,
    durability: 700,
    rarityCode: "SR",
    stats: [{ label: "Fusion", value: "+1 slot" }, { label: "Cost mod", value: "-1 fuse" }],
    cardText: "Reduce the cost of your next Fusion Pal by 2. Fusion Pals gain +100 power on entry.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
];

export const EVENT_CARDS: EventCardData[] = [
  {
    cardNumber: "ECE-001",
    name: "Sphere Rush",
    eventType: "Instant",
    color: "Blue",
    cost: 1,
    rarityCode: "C",
    cardText: "Search your deck for a Sphere Gear card and put it into your hand.",
    flavorText: "Assembly line goes brrr.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECE-002",
    name: "Cake Delivery",
    eventType: "Support",
    color: "Green",
    cost: 2,
    rarityCode: "C",
    cardText: "Restore 200 power to one of your Pals. If the target is Breeding Farm-linked, draw a card.",
    flavorText: "Berries, honey, red berry sauce, and a suspicious amount of milk.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECE-003",
    name: "Palworld Tournament",
    eventType: "Ongoing",
    color: "Red",
    cost: 3,
    rarityCode: "U",
    cardText: "While this Event is in play, all Pals gain +50 power. At end of your turn, discard 1 card.",
    flavorText: "The stadium roars for Anubis.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECE-004",
    name: "Emergency Recall",
    eventType: "Reaction",
    color: "Blue",
    cost: 2,
    rarityCode: "U",
    cardText: "In response to a strike: return your active Pal to hand. Prevent all damage from that strike.",
    flavorText: "Palbox teleport secured.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECE-005",
    name: "Ancient Technology",
    eventType: "Support",
    color: "Purple",
    cost: 3,
    rarityCode: "R",
    cardText: "Draw 2 cards. If you control a Legendary Pal, draw 1 additional card.",
    flavorText: "Blueprints from a forgotten age.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECE-006",
    name: "Meteor Strike",
    eventType: "Instant",
    color: "Red",
    cost: 5,
    rarityCode: "R",
    cardText: "Deal 400 damage split any way among opposing Pals and Structures.",
    flavorText: "The sky screams.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECE-007",
    name: "Frostallion Blizzard",
    eventType: "Instant",
    color: "Blue",
    cost: 4,
    rarityCode: "R",
    cardText: "Freeze all opposing Pals: they cannot strike or activate abilities next turn.",
    flavorText: "The night wind carries her wings.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECE-008",
    name: "Eidrolon Ascendance",
    eventType: "Instant",
    color: "Purple",
    cost: 8,
    rarityCode: "TD",
    cardText: "Discard 2 cards: your Eidrolon gains +400 power and ignores all Event card effects this turn.",
    flavorText: "The World Tree remembers.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECE-009",
    name: "Free Pal Alliance Raid",
    eventType: "Ongoing",
    color: "Green",
    cost: 4,
    rarityCode: "U",
    cardText: "Each turn, one of your Pals is protected from removal. Discard this Event when a Boss is defeated.",
    flavorText: "The PIDF stands ready.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
  {
    cardNumber: "ECE-010",
    name: "Void Rift",
    eventType: "Reaction",
    color: "Purple",
    cost: 3,
    rarityCode: "SR",
    cardText: "Cancel one opposing Event card and remove it from the game.",
    flavorText: "Reality declines your request.",
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  },
];
export const TYPE_CHART: Record<Element, Partial<Record<Element, number>>> = {
  Neutral:  {},
  Fire:     { Grass: 2, Ice: 2, Water: 0.5, Dragon: 0.5 },
  Water:    { Fire: 2, Ground: 2, Grass: 0.5, Electric: 0.5 },
  Grass:    { Water: 2, Ground: 2, Fire: 0.5, Ice: 0.5 },
  Electric: { Water: 2, Ground: 0.5, Grass: 0.5 },
  Ice:      { Grass: 2, Dragon: 2, Fire: 0.5 },
  Ground:   { Fire: 2, Electric: 2, Grass: 0.5 },
  Dark:     { Dark: 0.5, Neutral: 2 },
  Dragon:   { Dragon: 2, Ice: 0.5 },
};

// Curated real-name seeds; the rest are generated so total >= 170.
const NAMED_SEEDS = [
  "Lamball","Cattiva","Chikipi","Lifmunk","Foxparks","Fuack","Sparkit","Tanzee","Rooby","Pengullet",
  "Penking","Jolthog","Gumoss","Vixy","Hoocrates","Teafant","Depresso","Cremis","Daedream","Rushoar",
  "Nox","Fuddler","Killamari","Mau","Celaray","Direhowl","Tocotoco","Flopie","Mozzarina","Bristla",
  "Gobfin","Hangyu","Mossanda","Woolipop","Caprity","Melpaca","Eikthyrdeer","Nitewing","Ribbuny","Incineram",
  "Cinnamoth","Arsox","Dumud","Cawgnito","Leezpunk","Loupmoon","Galeclaw","Robinquill","Broncherry","Kelpsea",
  "Azurobe","Cryolinx","Blazehowl","Relaxaurus","Broncherry Aqua","Petallia","Reptyro","Kingpaca","Mammorest","Wumpo",
  "Warsect","Fenglope","Felbat","Quivern","Blazamut","Helzephyr","Astegon","Menasting","Anubis","Jormuntide",
  "Suzaku","Grizzbolt","Lyleen","Faleris","Orserk","Shadowbeak","Paladius","Necromus","Frostallion","Jetragon",
  "Beakon","Ragnahawk","Vanwyrm","Bushi","Univolt","Foxcicle","Pyrin","Reindrix","Rayhound","Kitsun",
  "Dazzi","Lunaris","Dinossom","Surfent","Maraith","Digtoise","Tombat","Lovander","Flambelle","Vaelet",
  "Sibelyx","Elphidran","Kelpsea Ignis","Dinossom Lux","Pyrin Noct","Mau Cryst","Katress","Wixen","Verdash","Vanwyrm Cryst",
  "Bushi Noct","Jormuntide Ignis","Frostallion Noct","Ragnahawk Aqua","Lyleen Noct","Suzaku Aqua","Blazehowl Noct",
  "Astegon Wing","Menasting Frost","Shadowbeak Lord","Beakon Storm","Faleris Sky","Jetragon Ignis","Grizzbolt Rex","Warsect Alpha",
  "Yakumo","Selyne","Bellanoir","Bellanoir Libero","Xenolord","Xenogard","Croajiro","Broncherry Ivory","Sootseer",
  "Mimog","Splatterina","Hopeful Yakumo","Selyne Nyx","Mossanda Lux","Petallia Terra",
  "Grintale","Boltmane","Vixy Lux","Rooby Blaze","Depresso Noct","Cattiva Ignis","Sparkit Storm",
  "Nox Umbra","Gumoss Verdant","Tocotoco Sky","Ribbuny Cryst","Killamari Deep","Direhowl Alpha","Cinnamoth Aureus","Loupmoon Noct","Kelpsea Abyss","Fuddler Mire",
  // Feybreak / World Tree era Pals confirmed by Pocketpair
  "Bastigor","Flaracle",
  // Palworld OFFICIAL CARD GAME additions

  "Chillet","Gorirat","Jetragon Astral","Frostallion Titan","Paladius Radiant","Xenolord Awakened",
  "Croajiro Aqua","Sootseer Ember","Hopeful Selyne","Boltmane Storm",
  "Lamball Cryst","Cattiva Noct","Foxparks Ignis","Pengullet Frost","Vixy Aureus","Rooby Ember","Rushoar Alpha","Mau Umbra",
  // 2026 expansion — new pals, fusion variants & lucky forms
  "Eidrolon","Lucky Lamball","Lucky Foxparks","Lucky Cattiva","Lucky Jetragon","Lucky Frostallion","Lucky Anubis","Lucky Shadowbeak",
  
  "Astral Beakon","Astral Ragnahawk","Cryo Reindrix","Cryo Foxcicle",
  "Storm Rayhound","Storm Univolt",
  // Official Paldeck additions
  "Panthalus","Sekhmet",

];


const PARTNER_SKILLS: Record<Element, string[]> = {
  Neutral:  ["Steady Companion","Bulwark","Ranger's Focus"],
  Fire:     ["Kindling","Forge Master","Blaze Rush"],
  Water:    ["Aquatic Torrent","Deep Current","Rain Caller"],
  Grass:    ["Photosynthesis","Verdant Growth","Botanist"],
  Electric: ["Overcharge","Static Surge","Volt Rider"],
  Ice:      ["Frost Coat","Chill Wave","Cryo Guardian"],
  Ground:   ["Miner's Grip","Quake Stomp","Tectonic Shift"],
  Dark:     ["Umbral Veil","Nightmare Whisper","Void Pact"],
  Dragon:   ["Dragon Wing","Ancient Roar","Sky Sovereign"],
};

const WORK_TAGS: Record<Element, string[]> = {
  Neutral:  ["Handiwork","Transporting"],
  Fire:     ["Kindling","Smelting"],
  Water:    ["Watering","Cooling"],
  Grass:    ["Planting","Gathering"],
  Electric: ["Generating Electricity"],
  Ice:      ["Cooling","Watering"],
  Ground:   ["Mining","Handiwork"],
  Dark:     ["Farming","Medicine Production"],
  Dragon:   ["Lumbering","Mining","Handiwork"],
};

// Fast, deterministic hash from a string.
function hash(str: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function pick<T>(arr: T[], seed: number): T {
  return arr[seed % arr.length];
}

function slugify(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

export interface SkillNode {
  tier: number;
  name: string;
  effect: string;
  cost: number;
  unlockLevel: number;
}

export interface Pal {
  id: number;
  slug: string;
  name: string;
  element: Element;
  secondary?: Element;
  rarity: "Common" | "Uncommon" | "Rare" | "Legendary";
  hp: number;
  attack: number;
  defense: number;
  speed: number;
  rideSpeed: number;
  stamina: number;
  captureRate: number; // 1-100 (higher = easier to capture)
  foodRequirement: number; // 1-10 slots
  workSpeed: number; // 40-260
  partnerSkill: string;
  work: string[];
  moves: { name: string; power: number; element: Element; cooldown: number }[];
  drops: string[];
  spawn: string;
  description: string;
  pveRating: number; // 1-10
  pvpRating: number;
  baseRating: number;
  mountRating: number;
  skillTree: SkillNode[];
  tcg: PalCardData;
}

const MOVES_POOL = [
  "Slam","Fireball","Aqua Burst","Vine Whip","Thunder Bolt","Ice Shard","Rock Blast",
  "Shadow Ball","Dragon Breath","Cross Slash","Meteor","Tidal Wave","Solar Beam",
  "Volt Cannon","Blizzard Spike","Earth Fissure","Umbral Lance","Draconic Rage",
];

const REGIONS = [
  "Windswept Hills","Verdant Brook","Sea Breeze Archipelago","Bamboo Groves","Moonless Shore",
  "Sanctuary of Ashes","Tundra Ridge","Sulfur Highlands","Astral Mountain","Feybreak Isles",
  "Sakurajima Coast","Marsh of Whispers","Deep Sand Dunes","Twilight Cliffs",
  "World Tree","Oil Rig Stronghold","Forgotten Mines","Ancient Ritual Site",
];

const RARITY_TIER: Pal["rarity"][] = ["Common","Common","Common","Uncommon","Uncommon","Rare","Rare","Legendary"];

const CARD_COLOR_BY_ELEMENT: Record<Element, CardColor> = {
  Neutral: "Colorless",
  Fire: "Red",
  Water: "Blue",
  Grass: "Green",
  Electric: "Blue",
  Ice: "Blue",
  Ground: "Green",
  Dark: "Purple",
  Dragon: "Purple",
};

// Legacy third-party thumbnail overrides are no longer needed: every printed card
// now resolves to its official scan via officialCardForPal()/officialCardImage().
const TCG_IMAGE_OVERRIDES: Record<string, Partial<PalCardData>> = {};

function SET_LABEL(code: string): string {
  return SET_NAMES[code] ?? code;
}

function OFFICIAL_VARIANTS(baseCode: string): { code: string; rarity: string }[] {
  return OFFICIAL_CARDS.filter((c) => c.base === baseCode && c.variant).map((c) => ({ code: c.code, rarity: c.rarity }));
}

function rarityCode(rarity: Pal["rarity"]): PalCardData["rarityCode"] {
  if (rarity === "Legendary") return "SR";
  if (rarity === "Rare") return "R";
  if (rarity === "Uncommon") return "U";
  return "C";
}

function buildCardData(id: number, slug: string, name: string, element: Element, rarity: Pal["rarity"], partnerSkill: string, work: string[], hp: number, attack: number, h: number): PalCardData {
  const lucky = /^Lucky /.test(name);
  const base: PalCardData = {
    cardNumber: `EC-${String(id).padStart(3, "0")}`,
    set: "Pal Stars Paldeck / OCG-style index",
    cardTitle: partnerSkill,
    cardType: lucky ? "Lucky Pal" : "Pal",
    color: CARD_COLOR_BY_ELEMENT[element],
    cost: Math.max(1, Math.min(10, Math.round((hp + attack) / 150))),
    power: Math.round((attack * 2 + hp) / 25) * 100,
    strike: Math.max(1, Math.min(5, 1 + Math.floor(attack / 120))),
    rarityCode: lucky ? "R" : rarityCode(rarity),
    workIcons: work,
    keywords: [rarity === "Legendary" ? "Brave" : "Vigilance", element === "Dark" ? "Nocturnal" : element === "Dragon" ? "Assault" : "Serious"],
    cardText: `CONT: While ${name} is active, ${partnerSkill} boosts ${element} actions and improves ${work[0] ?? "base"} efficiency.`,
    flavorText: `${name}'s Pal Stars card entry mirrors Palworld Official Card Game fields: color, cost, power, strike, rarity, work icons and rules text.`,
    sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
  };
  // Exact printed card identity, when this Pal exists in the official card game.
  const official = officialCardForPal(name);
  if (official) {
    const variants = OFFICIAL_VARIANTS(official.base);
    return {
      ...base,
      cardNumber: official.code,
      set: SET_LABEL(official.set),
      cardTitle: official.subtitle || base.cardTitle,
      color: official.color,
      cost: official.cost ?? base.cost,
      power: official.power ?? base.power,
      strike: official.strike ?? base.strike,
      rarityCode: official.rarity as PalCardData["rarityCode"],
      cardText: official.effect || base.cardText,
      flavorText: official.flavor || base.flavorText,
      imageUrl: officialCardImage(official.code),
      official: true,
      variants,
      sourceUrl: "https://en.palworld-official-cardgame.com/cardlist",
    };
  }

  const override = TCG_IMAGE_OVERRIDES[slug];
  return {
    ...base,
    cardNumber: `PSD-${String(id).padStart(3, "0")}`,
    rarityCode: "PSD",
    set: "Pal Stars Paldeck index — no printed OCG card",
    ...override,
    sourceUrl: override?.sourceUrl ?? base.sourceUrl,
  };
}

function generatePal(id: number, name: string): Pal {
  const slug = slugify(name);
  const h = hash(slug + ":" + id);
  // Verified in-game data wins over the procedural seed (see REAL_PAL_DATA).
  const real = REAL_PAL_DATA[slug];
  const element = real?.element ?? ELEMENTS[h % ELEMENTS.length];
  const secondaryRoll = (h >>> 4) % 5;
  const secondary = real ? real.secondary : secondaryRoll === 0 ? ELEMENTS[(h >>> 7) % ELEMENTS.length] : undefined;
  const rarity = real?.rarity ?? RARITY_TIER[(h >>> 3) % RARITY_TIER.length];


  const rarityBoost = rarity === "Legendary" ? 200 : rarity === "Rare" ? 100 : rarity === "Uncommon" ? 40 : 0;

  const hp = 100 + ((h >>> 5) % 450) + rarityBoost;
  const attack = 60 + ((h >>> 8) % 240) + Math.floor(rarityBoost / 2);
  const defense = 50 + ((h >>> 11) % 200) + Math.floor(rarityBoost / 2);
  const speed = 40 + ((h >>> 13) % 120);
  const rideSpeed = 400 + ((h >>> 15) % 900);
  const stamina = 80 + ((h >>> 17) % 220);

  const partnerSkill = pick(PARTNER_SKILLS[element], h >>> 6);
  const work = WORK_TAGS[element];

  const moves = [0,1,2,3].map((i) => {
    const seed = h >>> (i * 3 + 2);
    const mvName = MOVES_POOL[seed % MOVES_POOL.length];
    const mvElement = i === 0 ? element : ELEMENTS[(seed >>> 4) % ELEMENTS.length];
    return {
      name: mvName,
      power: 30 + ((seed >>> 8) % 140) + (i === 3 ? 60 : 0),
      element: mvElement,
      cooldown: 4 + ((seed >>> 12) % 24),
    };
  });

  const drops = [
    element === "Fire" ? "Flame Organ" : element === "Water" ? "Pure Water" : element === "Electric" ? "Electric Organ" : element === "Ice" ? "Ice Organ" : "Leather",
    rarity === "Legendary" ? "Ancient Civilization Part" : rarity === "Rare" ? "Precious Metal" : "Bone",
  ];

  const spawn = pick(REGIONS, h >>> 9);
  const description = `${name} is a ${rarity.toLowerCase()} ${element.toLowerCase()} Pal known for its ${partnerSkill.toLowerCase()} ability. Frequently sighted in the ${spawn}.`;

  const pveRating = Math.min(10, 4 + Math.floor(rarityBoost / 40) + ((h >>> 19) % 3));
  const pvpRating = Math.min(10, 3 + Math.floor(rarityBoost / 50) + ((h >>> 21) % 4));
  const baseRating = Math.min(10, 4 + ((h >>> 23) % 5));
  const mountRating = Math.min(10, Math.floor(rideSpeed / 130));

  // Capture rate: legendaries very hard, commons easy. Range ~5-95.
  const rarityCap = rarity === "Legendary" ? 8 : rarity === "Rare" ? 22 : rarity === "Uncommon" ? 45 : 65;
  const captureRate = Math.max(3, Math.min(95, rarityCap + ((h >>> 25) % 20) - 10));
  const foodRequirement = 1 + ((h >>> 27) % 6) + (rarity === "Legendary" ? 3 : rarity === "Rare" ? 2 : 0);
  const workSpeed = 40 + ((h >>> 12) % 180) + Math.floor(rarityBoost / 3);

  // Skill tree: 6 nodes across 3 tiers, deterministic
  const SKILL_POOL: Record<Element, string[]> = {
    Neutral: ["Endurance","Grit","Ranger Focus","Steady Aim","Second Wind","Bulwark"],
    Fire: ["Kindle","Ignition","Blaze Aura","Inferno Core","Molten Skin","Solar Flare"],
    Water: ["Aqua Veil","Torrent","Deep Breath","Hydro Cannon","Rain Guard","Tidal Fury"],
    Grass: ["Rooting","Bloom","Photosynthesis","Vine Trap","Verdant Heal","Solar Bloom"],
    Electric: ["Static","Overcharge","Chain Bolt","Volt Rush","Ion Field","Storm Core"],
    Ice: ["Frostbite","Chill","Cryo Armor","Blizzard","Ice Mirror","Absolute Zero"],
    Ground: ["Sturdy","Quake","Earthwall","Miner Grip","Rockfall","Tectonic Roar"],
    Dark: ["Shadow Step","Umbral Veil","Nightmare","Void Pulse","Phantom Edge","Eclipse"],
    Dragon: ["Draco Scale","Roar","Sky Sovereign","Ancient Wrath","Dragon Force","Cataclysm"],
  };
  const skillPool = SKILL_POOL[element];
  const skillTree: SkillNode[] = skillPool.slice(0, 6).map((skName, i) => ({
    tier: Math.floor(i / 2) + 1,
    name: skName,
    effect: i === 0 ? `+${5 + ((h >>> (i+2)) % 10)}% ${element} damage`
      : i === 1 ? `-${5 + ((h >>> (i+3)) % 8)}% stamina cost`
      : i === 2 ? `+${8 + ((h >>> (i+4)) % 12)}% work speed`
      : i === 3 ? `Grants ${skName} passive on partner`
      : i === 4 ? `+${10 + ((h >>> (i+5)) % 15)}% defense vs ${element}`
      : `Unlocks ultimate: ${skName} (signature move)`,
    cost: (Math.floor(i / 2) + 1) * 2,
    unlockLevel: 5 + i * 8 + (rarity === "Legendary" ? 5 : 0),
  }));

  const tcg = buildCardData(id, slug, name, element, rarity, partnerSkill, work, hp, attack, h);

  return {
    id, slug, name, element, secondary, rarity,
    hp, attack, defense, speed, rideSpeed, stamina,
    captureRate, foodRequirement, workSpeed,
    partnerSkill, work, moves, drops, spawn, description,
    pveRating, pvpRating, baseRating, mountRating, skillTree, tcg,
    ...(real ?? {}),
  };
}




// Names that must never appear in the roster: they are not real Palworld Pals.
// Procedural variant generation would otherwise mint them from the prefix pool.
const EXCLUDED_NAMES = new Set([
  "Neo Jolthog",
  "Neo Penking",
  "Neo Depresso",
  "Neo Teafant",
  "Neo Hoocrates",
  "Neo Gumoss",
  "Neo Vixy",
  "Neo Fuddler",
  "Neo Rushoar",
  "Neo Killamari",
  "Neo Mau",
  "Neo Nox",
  "Neo Cattiva",
  "Neo Celeray",
  "Neo Chikipi",
  "Neo Cremis",
  "Neo Daedream",
  "Neo Direhowl",
  "Neo Flopie",
  "Neo Foxparks",
  "Neo Fuack",
  "Neo Lamball",
  "Neo Lifmunk",
  "Neo Mozzarina",
  "Neo Pengullet",
  "Neo Rooby",
  "Neo Sparkit",
  "Neo Tanzee",
  "Neo Tocotoco",
  // Fan-invented "Void <base Pal>" variants — not real Palworld Pals.
  "Void Cattiva",
  "Void Celeray",
  "Void Celaray",
  "Void Chikipi",
  "Void Cremis",
  "Void Daedream",
  "Void Depresso",
  "Void Foxparks",
  "Void Fuack",
  "Void Fuddler",
  "Void Gumoss",
  "Void Hoocrates",
  "Void Jolthog",
  "Void Killamari",
  "Void Lamball",
  "Void Lifmunk",
  "Void Nox",
  "Void Pengullet",
  "Void Penking",
  "Void Rooby",
  "Void Rushoar",
  "Void Sparkit",
  "Void Tanzee",
  "Void Teafant",
  "Void Vixy",
]);

/**
 * Verified in-game data for Pals whose real stat sheets are published by
 * Pocketpair. These values replace the procedural seed so the database shows
 * the actual game numbers instead of generated ones.
 */
const REAL_PAL_DATA: Record<string, Partial<Pal>> = {
  bastigor: {
    element: "Ice",
    rarity: "Legendary",
    hp: 140,
    attack: 130,
    defense: 120,
    stamina: 100,
    speed: 100,
    rideSpeed: 950,
    captureRate: 8,
    foodRequirement: 8,
    workSpeed: 130,
    partnerSkill: "Glacial Fists",
    work: ["Lumbering 3", "Mining 3", "Cooling 4"],
    drops: ["Ice Organ", "Leather"],
    spawn: "World Tree (roaming) — also hatches from a Huge Frozen Egg near the tower",
    moves: [
      { name: "Stone Cannon", power: 70, element: "Ground", cooldown: 15 },
      { name: "Iceberg", power: 70, element: "Ice", cooldown: 15 },
      { name: "Rockburst", power: 130, element: "Ground", cooldown: 35 },
      { name: "Crystal Wing", power: 90, element: "Ice", cooldown: 22 },
    ],
    description:
      "Bastigor is a hostile bipedal liger-like Ice Pal introduced with Feybreak, with leonine light-green fur and a brown loincloth. The sight of anything round gives it an uncontrollable urge to punch it, which makes building a snowman its hardest challenge. It roams the World Tree, is rideable, and breeds from Frostallion + Anubis.",
    pveRating: 9,
    pvpRating: 8,
    baseRating: 8,
    mountRating: 8,
  },
  flaracle: {
    element: "Fire",
    rarity: "Rare",
    hp: 120,
    attack: 125,
    defense: 105,
    stamina: 100,
    speed: 95,
    rideSpeed: 700,
    captureRate: 15,
    foodRequirement: 6,
    workSpeed: 120,
    partnerSkill: "Flame Divination",
    work: ["Kindling 3", "Gathering 2"],
    drops: ["Flame Organ", "Leather", "Fire Radiant Gem"],
    spawn: "World Tree region",
    moves: [
      { name: "Fire Ball", power: 65, element: "Fire", cooldown: 12 },
      { name: "Flare Arrow", power: 45, element: "Fire", cooldown: 8 },
      { name: "Ignis Blast", power: 100, element: "Fire", cooldown: 26 },
      { name: "Spirit Flame", power: 120, element: "Fire", cooldown: 34 },
    ],
    description:
      "Flaracle is a Fire element Pal that focuses its mind and reads the future through flames. Should it glimpse an inconvenient destiny, it relentlessly rerolls the prophecy until it gets a favourable result — which is why the future of Flaracle is always exceptionally bright. High-level Alpha and Boss Flaracle drop Flame Organ, Leather, World Tree Holy Water, Fire Radiant Gems and Ancient Relics.",
    pveRating: 8,
    pvpRating: 7,
    baseRating: 7,
    mountRating: 5,
  },
};


function buildRoster(): Pal[] {
  // "Neo <Pal>" and "<Pal> Prime" / "Prime <Pal>" entries are fan inventions,
  // not real Palworld Pals — never generate or keep any of them.
  const isFake = (n: string) =>
    EXCLUDED_NAMES.has(n) ||
    /^Neo\s/i.test(n) ||
    /^Void\s/i.test(n) ||
    // Fan-invented "Fusion <Pal>" and "<Pal> Void/Voidlord" variants.
    /^Fusion\s/i.test(n) ||
    /\bVoid(lord)?$/i.test(n) ||
    /(^|\s)Solar(\s|$)/i.test(n) ||
    /(^|\s)Prime(\s|$)/i.test(n);
  // Officially confirmed Pals only. No procedural prefix padding ("Storm X",
  // "Alpha X", ...) — those are fan inventions and must never ship.
  const names: string[] = NAMED_SEEDS.filter((n) => !isFake(n));

  return names.map((n, idx) => {
    const p = generatePal(idx + 1, n);
    // Force legendary tier for signature Eidrolon cards
    if (/^Eidrolon/i.test(n) || /^Fusion /.test(n)) {
      p.rarity = "Legendary";
      p.hp = Math.max(p.hp, 720);
      p.attack = Math.max(p.attack, 340);
      p.defense = Math.max(p.defense, 300);
      p.pveRating = 10; p.pvpRating = 10; p.baseRating = 9; p.mountRating = 10;
      p.captureRate = Math.min(p.captureRate, 6);
      if (!p.tcg.official) {
        p.tcg.rarityCode = "PSD";
        p.tcg.power = Math.max(p.tcg.power, 2200);
        p.tcg.strike = 5;
        p.tcg.cost = 10;
      }
      if (n === "Eidrolon") {
        p.description = "Eidrolon is the mythic apex Pal of the Palpagos Islands — a fusion legend whose elemental core adapts to its wielder. Sightings are unconfirmed outside of raid sanctums.";
        p.partnerSkill = "Eidrolon Ascendance";
        p.spawn = "World Tree";
        p.tcg.cardNumber = "PSD-242";
        p.tcg.cardTitle = "Eidrolon Ascendance";
        p.tcg.cardText = "CONT: Eidrolon cycles one element each turn. When it changes element, draw a card and this Pal gains +300 power until end of turn.";
        p.tcg.flavorText = "A mythic fan-card entry for Pal Stars: the World Tree fusion legend at the edge of Palpagos.";
      }
    }
    if (/^Lucky /.test(n)) {
      p.rarity = "Rare";
      p.captureRate = Math.min(p.captureRate, 10);
      p.workSpeed = Math.round(p.workSpeed * 1.3);
      p.description = `${n} is a shiny Lucky variant with a golden aura and +30% work speed. Extremely rare spawn.`;
    }
    return p;
  });
}


export const PALS: Pal[] = buildRoster();
export const PAL_BY_SLUG: Record<string, Pal> = Object.fromEntries(PALS.map((p) => [p.slug, p]));

export function searchPals(query: unknown, element?: Element, rarity?: Pal["rarity"]): Pal[] {
  // Crawled/shared URLs can repeat a param (?q=a&q=b) or use array syntax, which
  // arrives as an array and used to throw during SSR. Coerce instead of crashing.
  const raw = typeof query === "string" ? query : Array.isArray(query) ? String(query[0] ?? "") : "";
  const q = raw.trim().toLowerCase();
  return PALS.filter((p) => {
    if (element && p.element !== element) return false;
    if (rarity && p.rarity !== rarity) return false;
    if (!q) return true;
    return p.name.toLowerCase().includes(q)
      || p.partnerSkill.toLowerCase().includes(q)
      || p.spawn.toLowerCase().includes(q)
      || p.tcg.cardNumber.toLowerCase().includes(q)
      || p.tcg.cardTitle.toLowerCase().includes(q)
      || p.moves.some((m) => m.name.toLowerCase().includes(q))
      || p.work.some((w) => w.toLowerCase().includes(q));
  });
}

export function typeMultiplier(attacker: Element, defender: Element): number {
  return TYPE_CHART[attacker]?.[defender] ?? 1;
}
